"use client";

import { getApp, getApps, initializeApp } from "firebase/app";
import {
  deleteToken,
  getMessaging,
  getToken,
  isSupported,
  type Messaging,
} from "firebase/messaging";
import {
  firebaseVapidKey,
  firebaseWebConfig,
  isFirebaseWebMessagingConfigured,
} from "./config";

let messagingPromise: Promise<Messaging | null> | null = null;

const getBrowserMessaging = async (): Promise<Messaging | null> => {
  if (messagingPromise) return messagingPromise;

  messagingPromise = (async () => {
    try {
      if (
        typeof window === "undefined" ||
        !isFirebaseWebMessagingConfigured ||
        !(await isSupported())
      ) {
        return null;
      }

      const app =
        getApps().length > 0 ? getApp() : initializeApp(firebaseWebConfig);
      return getMessaging(app);
    } catch {
      messagingPromise = null;
      return null;
    }
  })();

  return messagingPromise;
};

const registerMessagingServiceWorker = async () => {
  if (!("serviceWorker" in navigator)) return null;

  const registration = await navigator.serviceWorker.register(
    "/firebase-messaging-sw.js",
    { scope: "/" },
  );
  await navigator.serviceWorker.ready;
  return registration;
};

export const supportsChatNotifications = async (): Promise<boolean> => {
  if (
    typeof window === "undefined" ||
    !("Notification" in window) ||
    !("serviceWorker" in navigator) ||
    !isFirebaseWebMessagingConfigured
  ) {
    return false;
  }

  try {
    return await isSupported();
  } catch {
    return false;
  }
};

export const getChatNotificationToken = async ({
  requestPermission,
}: {
  requestPermission: boolean;
}): Promise<string | null> => {
  if (
    typeof window === "undefined" ||
    !("Notification" in window) ||
    !("serviceWorker" in navigator) ||
    !isFirebaseWebMessagingConfigured
  ) {
    return null;
  }

  let permission = Notification.permission;
  if (requestPermission && permission === "default") {
    permission = await Notification.requestPermission();
  }
  if (permission !== "granted") return null;
  if (!(await supportsChatNotifications())) return null;

  const [messaging, serviceWorkerRegistration] = await Promise.all([
    getBrowserMessaging(),
    registerMessagingServiceWorker(),
  ]);
  if (!messaging || !serviceWorkerRegistration) return null;

  return getToken(messaging, {
    vapidKey: firebaseVapidKey,
    serviceWorkerRegistration,
  });
};

export const deleteChatNotificationToken = async (): Promise<void> => {
  const messaging = await getBrowserMessaging();
  if (messaging) await deleteToken(messaging);
};
